import {
    Dialog,
    Button,
    IconButton,
    Stack,
    Box,
    Typography,
    Tooltip,
} from "@mui/material";
import {useRef} from "react";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon, InformationCircleIcon} from "@hugeicons/core-free-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {VariableTextField} from "../../pages/project/flow/edit-menu/components/VariableTextField";
import {useSyncedScroll} from "../../hooks/useSyncedScroll";
import {MARKDOWN_PREVIEW_SX} from "../markdown/markdownPreviewSx";

const kickerSx = {
    fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: "#8A8AA0",
    textTransform: "uppercase", px: 2, pt: 1.5, pb: 0.75,
};

export const MarkdownEditorModal = ({
                                        open,
                                        onClose,
                                        title = "System prompt",
                                        value,
                                        onChange,
                                        variables = [],
                                        placeholder = "The prompt text / instructions...",
                                    }) => {
    const editorBoxRef = useRef(null);
    const previewRef = useRef(null);
    const syncScroll = useSyncedScroll();

    const getEditor = () => editorBoxRef.current?.querySelector("textarea");

    const handleEditorScroll = () => syncScroll(getEditor(), previewRef.current);
    const handlePreviewScroll = () => syncScroll(previewRef.current, getEditor());

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: {xs: "calc(100% - 24px)", md: "min(1200px, 94vw)"},
                    maxHeight: "88vh", borderRadius: "20px",
                    boxShadow: "0 24px 70px rgba(35, 38, 77, 0.22)", p: {xs: 2.5, md: 3},
                },
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{mb: 2.5}}>
                <Typography sx={{fontSize: 16, fontWeight: 700, color: "#23264D"}}>{title}</Typography>
                <IconButton
                    onClick={onClose}
                    sx={{width: 34, height: 34, borderRadius: "10px", border: "1px solid #ECECF3", color: "#8A8AA0", flexShrink: 0}}
                >
                    <HugeiconsIcon icon={Cancel01Icon} size={18}/>
                </IconButton>
            </Stack>

            <Stack direction="row" sx={{
                height: "60vh", minHeight: 320, bgcolor: "#fff", border: "1px solid #ECECF3", borderRadius: "14px", overflow: "hidden",
            }}>
                <Box ref={editorBoxRef} sx={{width: "50%", flexShrink: 0, display: "flex", flexDirection: "column", overflow: "hidden"}}>
                    <Stack direction="row" gap={0.5} alignItems="center">
                        <Typography sx={kickerSx}>Plain text</Typography>
                        <Tooltip
                            title={
                                <Stack gap={0.3} sx={{py: 0.5}}>
                                    <Typography variant="caption"># Heading 1 &nbsp;&nbsp;## Heading 2</Typography>
                                    <Typography variant="caption">**bold** &nbsp;&nbsp;*italic*</Typography>
                                    <Typography variant="caption">`inline code`</Typography>
                                    <Typography variant="caption">- list item &nbsp;&nbsp;1. numbered</Typography>
                                    <Typography variant="caption">[label](url)</Typography>
                                    <Typography variant="caption">&gt; blockquote</Typography>
                                    <Typography variant="caption">--- (horizontal rule)</Typography>
                                </Stack>
                            }
                            arrow
                            placement="right"
                        >
                            <HugeiconsIcon icon={InformationCircleIcon} size={13} style={{cursor: "help", opacity: 0.6}}/>
                        </Tooltip>
                    </Stack>
                    <VariableTextField
                        autoFocus
                        fullWidth
                        multiline
                        value={value}
                        onChange={(v) => onChange({target: {value: v}})}
                        placeholder={placeholder}
                        variables={variables}
                        inputProps={{onScroll: handleEditorScroll}}
                        InputProps={{
                            disableUnderline: true,
                            sx: {
                                fontFamily: "inherit", fontSize: 13, lineHeight: 1.8, color: "#23264D",
                                height: "100%", alignItems: "flex-start", px: 2, pb: 2,
                            },
                        }}
                        variant="standard"
                        sx={{
                            flex: 1, minHeight: 0,
                            "& .MuiInputBase-root": {height: "100%"},
                            "& .MuiInputBase-root:before, & .MuiInputBase-root:after": {display: "none"},
                            "& textarea": {height: "100% !important", overflow: "auto !important"},
                        }}
                    />
                </Box>

                <Box sx={{width: "50%", flexShrink: 0, display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: "#FAFAFD"}}>
                    <Typography sx={kickerSx}>Markdown preview</Typography>
                    <Box
                        ref={previewRef}
                        onScroll={handlePreviewScroll}
                        sx={{
                            flex: 1, minHeight: 0, overflow: "auto", px: 2, pb: 2,
                            fontSize: 13, color: "#23264D", lineHeight: 1.8,
                            ...MARKDOWN_PREVIEW_SX,
                        }}
                    >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {value || "*Nothing to preview*"}
                        </ReactMarkdown>
                    </Box>
                </Box>
            </Stack>

            <Stack direction="row" justifyContent="flex-end" sx={{mt: 1.5}}>
                <Button
                    onClick={onClose}
                    sx={{
                        textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: "10px",
                        color: "#374151", bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3"},
                    }}
                >
                    Done
                </Button>
            </Stack>
        </Dialog>
    );
};

export default MarkdownEditorModal;
